import { createCollapsibleBlock } from './collapsibleBlock';

interface TodayResultsConfig {
    visibleMatchesCount: number;
}

// Add type declaration for global window object
declare global {
    interface Window {
        setTodayResultsCollapsing: (config: TodayResultsConfig) => void;
    }
}

// Create and export the function
const setTodayResultsCollapsing = (config: TodayResultsConfig): void => {
    const wrapper = document.querySelector('.js-results-wrapper') as HTMLElement;
    if (!wrapper) return;

    const results = wrapper.querySelector('.js-results') as HTMLElement;
    const button = wrapper.querySelector('.js-results__toggle-button') as HTMLButtonElement;
    const matches = results.querySelectorAll('.js-results__match');
    
    const expandedHeight = wrapper.clientHeight;
    const buttonHeight = parseInt(window.getComputedStyle(button).height, 10);
    const collapsedClass = 'results-wrapper--collapsed';

    const buttonText = {
        collapse: button.getAttribute('data-text-collapse') || '',
        expand: button.getAttribute('data-text-expand') || ''
    };

    const getCollapsedHeight = (): number => {
        const scrollTop = document.documentElement.scrollTop;
        const lastVisibleMatch = matches[config.visibleMatchesCount - 1];
        const lastVisibleMatchBottom = lastVisibleMatch.getBoundingClientRect().bottom + scrollTop;
        const wrapperTop = wrapper.getBoundingClientRect().top + scrollTop;

        return lastVisibleMatchBottom - wrapperTop + buttonHeight;
    };

    if (matches.length <= config.visibleMatchesCount) {
        results.classList.add('results--no-collapsing');
        return;
    }

    wrapper.classList.add(collapsedClass);
    wrapper.style.height = `${getCollapsedHeight()}px`;
    results.classList.remove('results--no-collapsing');
    button.classList.remove('results__toggle-button--hidden');

    const collapsibleBlock = createCollapsibleBlock({
        block: wrapper,
        button,
        expandedHeight,
        collapsedHeight: getCollapsedHeight()
    });

    collapsibleBlock.setToggleButtonClickListeners({
        collapseHandler: () => {
            button.blur();
            button.style.visibility = 'hidden'; // Hide immediately
            wrapper.classList.add(collapsedClass);
            
            wrapper.addEventListener('transitionend', (e) => {
                if (e.propertyName === 'height') {
                    button.textContent = buttonText.collapse;
                    button.style.visibility = 'visible';
                }
            }, { once: true });
        },
        expandHandler: () => {
            button.blur();
            button.style.visibility = 'hidden'; // Hide immediately
            wrapper.classList.remove(collapsedClass);
            
            wrapper.addEventListener('transitionend', (e) => {
                if (e.propertyName === 'height') {
                    button.textContent = buttonText.expand;
                    button.style.visibility = 'visible';
                }
            }, { once: true });
        }
    });
};

// Export for both module and global usage
export { setTodayResultsCollapsing };
export default setTodayResultsCollapsing;
